'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Card } from '@morpho-org/ui-v6/server';
import { Icon } from '@repo/ui';
import { Alert } from '@repo/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import type { MarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { MarketLendSummaryCard } from '@/components/common/MarketLendSummary';
import MarketLendWithdrawReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketLendWithdrawReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { useMarketLendWithdrawForm } from '@/hooks/operation/market/v2/useMarketLendWithdrawForm';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import useFinalizeLabel from '@/hooks/operation/useFinalizeLabel';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketLendWithdrawCompletedParams, trackMarketLendWithdrawCompleted } from '@/lib/analytics/client/trackers/market-lend';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
interface MarketOperationLendWithdrawProps {
  marketId: MarketId;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  /** morpho-sdk MarketParams for the v2 flow (from the parent's API market). */
  marketParams: MarketParams | undefined;
  marketForRewards?: MarketForRewards | null;
  avgSupplyApy?: number | null;
  /** VRM-5363: restricted-jurisdiction verdict for this market's assets; blocks the CTA. */
  geoRestriction: MarketGeoRestriction;
  /**
   * The panel's one-sided gate has not answered yet. The form still renders so typed input
   * survives, but it cannot be finalized: the wallet's lend position is unconfirmed.
   */
  isPositionGatePending: boolean;
}

/**
 * Market-page exit from a direct lend position. The Position Manager only reaches
 * portfolio rows, so without this tab a lender has no way out from the market page.
 */
const MarketOperationLendWithdraw = t0 => {
  const $ = _c(79);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    marketParams,
    marketForRewards,
    avgSupplyApy,
    geoRestriction,
    isPositionGatePending
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const queryClient = useQueryClient();
  const {
    toggleDialog
  } = useDialogContext();
  const [isInputPending, setIsInputPending] = useState(false);
  let t1;
  if ($[0] !== loanAsset || $[1] !== marketParams) {
    t1 = {
      marketParams,
      loanAsset
    };
    $[0] = loanAsset;
    $[1] = marketParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    isMarketLendEnabled,
    inputWithdrawAmount,
    setInputWithdrawAmountManually,
    isEmpty,
    positionData,
    positionError,
    isPositionLoading,
    isReallocationLoading,
    currentSupply,
    afterSupply,
    maxWithdrawAssets,
    isMaxActive,
    onMaxClick,
    shouldUseSupplyShares,
    reallocations,
    showLiquidityWarning,
    buildErrorMessage,
    canTransferLoanAsset,
    isFinalizeDisabled,
    isButtonLoading,
    isDebouncing,
    reset
  } = useMarketLendWithdrawForm(t1);
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  let t2;
  if ($[3] !== isMarketLendEnabled || $[4] !== marketId || $[5] !== targetChain.id || $[6] !== targetChain.name) {
    t2 = {
      entryPoint: "market_page",
      action: "withdraw",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      source: "market_page",
      enabled: isMarketLendEnabled
    };
    $[3] = isMarketLendEnabled;
    $[4] = marketId;
    $[5] = targetChain.id;
    $[6] = targetChain.name;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useTrackMarketActionsV2EntryViewed(t2);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== account.isWrongChain || $[10] !== buildErrorMessage || $[11] !== isEmpty) {
    t4 = {
      operationLabel: "Withdraw",
      isInitialState: isEmpty,
      isNew: true,
      isEditing: false,
      localErrorMessage: buildErrorMessage,
      operations: t3,
      isPending: false,
      blockError: null,
      isWrongChain: account.isWrongChain,
      hasDisabledMessage: false,
      noReviewPrefix: true
    };
    $[9] = account.isWrongChain;
    $[10] = buildErrorMessage;
    $[11] = isEmpty;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const finalizeLabel = useFinalizeLabel(t4);
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  const t5 = isFinalizeDisabled || isPositionGatePending || !bundleCanTransfer;
  let t6;
  if ($[13] !== finalizeLabel || $[14] !== geoRestriction || $[15] !== isButtonLoading || $[16] !== t5 || $[17] !== tooltipContent) {
    t6 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: t5,
      isLoading: isButtonLoading,
      finalizeLabel,
      tooltipContent
    });
    $[13] = finalizeLabel;
    $[14] = geoRestriction;
    $[15] = isButtonLoading;
    $[16] = t5;
    $[17] = tooltipContent;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[19] !== t6) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t6);
    $[19] = t6;
    $[20] = geoActionProps;
    $[21] = shouldBlockFinalize;
  } else {
    geoActionProps = $[20];
    shouldBlockFinalize = $[21];
  }
  let t7;
  if ($[22] !== account.isContract || $[23] !== account.isSafe || $[24] !== avgSupplyApy || $[25] !== collateralAsset || $[26] !== inputWithdrawAmount || $[27] !== loanAsset || $[28] !== marketForRewards || $[29] !== marketId || $[30] !== marketParams || $[31] !== queryClient || $[32] !== reallocations || $[33] !== reset || $[34] !== shouldBlockFinalize || $[35] !== shouldUseSupplyShares || $[36] !== targetChain.id || $[37] !== targetChain.name || $[38] !== toggleDialog) {
    t7 = () => {
      if (!marketParams || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "market_page",
        action: "withdraw",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name,
        source: "market_page"
      });
      toggleDialog(<MarketLendWithdrawReviewDialogV2 marketParams={marketParams} amount={inputWithdrawAmount} shouldUseSupplyShares={shouldUseSupplyShares} reallocations={reallocations} collateralAsset={collateralAsset} loanAsset={loanAsset} marketForRewards={marketForRewards} avgSupplyApy={avgSupplyApy} onTxSuccess={withdrawAmount => {
        const params = buildMarketLendWithdrawCompletedParams({
          marketId,
          loanAsset,
          withdrawAmount,
          isFullExit: shouldUseSupplyShares,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "market_page",
          source: "market_page"
        });
        if (params) {
          trackMarketLendWithdrawCompleted(params);
        }
        queryClient.invalidateQueries();
        reset();
      }} entryPoint="market_page" />);
    };
    $[22] = account.isContract;
    $[23] = account.isSafe;
    $[24] = avgSupplyApy;
    $[25] = collateralAsset;
    $[26] = inputWithdrawAmount;
    $[27] = loanAsset;
    $[28] = marketForRewards;
    $[29] = marketId;
    $[30] = marketParams;
    $[31] = queryClient;
    $[32] = reallocations;
    $[33] = reset;
    $[34] = shouldBlockFinalize;
    $[35] = shouldUseSupplyShares;
    $[36] = targetChain.id;
    $[37] = targetChain.name;
    $[38] = toggleDialog;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  const handleFinalize = t7;
  if (positionError && !positionData) {
    let t8;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ErrorServiceUnavailable minHeight="600px" />;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    return t8;
  }
  const t8 = `Withdraw ${loanAsset.symbol}`;
  const t9 = isMarketLendEnabled && account.isConnected && (isPositionLoading || isReallocationLoading);
  let t10;
  if ($[41] !== inputWithdrawAmount || $[42] !== isMaxActive || $[43] !== loanAsset || $[44] !== maxWithdrawAssets || $[45] !== onMaxClick || $[46] !== setInputWithdrawAmountManually || $[47] !== t8 || $[48] !== t9) {
    t10 = <LargeAssetInput header={t8} token={loanAsset} amount={inputWithdrawAmount} onChange={setInputWithdrawAmountManually} onPendingChange={setIsInputPending} maxValue={maxWithdrawAssets} isMaxLoading={t9} isMax={isMaxActive} onMaxClick={onMaxClick} autoFocus={true} />;
    $[41] = inputWithdrawAmount;
    $[42] = isMaxActive;
    $[43] = loanAsset;
    $[44] = maxWithdrawAssets;
    $[45] = onMaxClick;
    $[46] = setInputWithdrawAmountManually;
    $[47] = t8;
    $[48] = t9;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== showLiquidityWarning) {
    t11 = showLiquidityWarning && <Card className="gap-2xs mt-xs bg-warning flex-row items-start shadow-none" padding="s" role="status" aria-live="polite" data-testid="market-lend-withdraw-liquidity-warning"><Icon className="text-warning-foreground" icon={<Alert />} aria-hidden="true" /><span className="text-2xs text-warning-foreground">Available liquidity limits the amount you can withdraw.</span></Card>;
    $[50] = showLiquidityWarning;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== canTransferLoanAsset || $[53] !== loanAsset.address) {
    t12 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[52] = canTransferLoanAsset;
    $[53] = loanAsset.address;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== t10 || $[56] !== t11 || $[57] !== t12) {
    t13 = <div className="flex flex-col gap-[2px]">{t10}{t11}{t12}</div>;
    $[55] = t10;
    $[56] = t11;
    $[57] = t12;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  let t14;
  if ($[59] !== afterSupply || $[60] !== avgSupplyApy || $[61] !== currentSupply || $[62] !== isPositionLoading || $[63] !== loanAsset || $[64] !== marketForRewards || $[65] !== marketId) {
    t14 = <MarketLendSummaryCard marketId={marketId} currentSupply={currentSupply} afterSupply={afterSupply} loanAsset={loanAsset} isLoading={isPositionLoading} marketForRewards={marketForRewards} avgSupplyApy={avgSupplyApy} />;
    $[59] = afterSupply;
    $[60] = avgSupplyApy;
    $[61] = currentSupply;
    $[62] = isPositionLoading;
    $[63] = loanAsset;
    $[64] = marketForRewards;
    $[65] = marketId;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  let t15;
  if ($[67] !== bundleCanTransfer) {
    t15 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[67] = bundleCanTransfer;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  let t16;
  if ($[69] !== geoActionProps || $[70] !== handleFinalize || $[71] !== isDebouncing || $[72] !== isInputPending) {
    t16 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={isInputPending} isValidating={isDebouncing} />;
    $[69] = geoActionProps;
    $[70] = handleFinalize;
    $[71] = isDebouncing;
    $[72] = isInputPending;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== t13 || $[75] !== t14 || $[76] !== t15 || $[77] !== t16) {
    t17 = <>{t13}{t14}{t15}{t16}</>;
    $[74] = t13;
    $[75] = t14;
    $[76] = t15;
    $[77] = t16;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  return t17;
};
export default memo(MarketOperationLendWithdraw);